import React, { useState } from "react";
import {
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PieChartIcon from "@mui/icons-material/PieChart";
import BarChartIcon from "@mui/icons-material/BarChart";

export const ProductCategoryDistributionChart = ({
  startDate,
  endDate,
  data,
}) => {
  const theme = useTheme();
  const [chartType, setChartType] = useState("pie");
  const [viewMode, setViewMode] = useState("quantity");

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Generate colors for pie chart slices
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    "#9c27b0", // purple
    "#795548", // brown
    "#607d8b", // blueGrey
    "#009688", // teal
  ];

  // Calculate totals for summary
  const totalQuantity = data.reduce((acc, item) => acc + item.value, 0);
  const totalRevenue = data.reduce((acc, item) => acc + item.revenue, 0);

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: "background.paper",
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {data.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Quantidade: <strong>{data.value}</strong> (
            {((data.value / totalQuantity) * 100).toFixed(1)}%)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Receita: <strong>R$ {data.revenue.toFixed(2)}</strong> (
            {((data.revenue / totalRevenue) * 100).toFixed(1)}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PieChartIcon
            size={24}
            color={theme.palette.primary.main}
            style={{ marginRight: 8 }}
          />
          <Typography variant="h6" fontWeight="600">
            Distribuição por Categoria
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="quantity" aria-label="quantity">
              Quantidade
            </ToggleButton>
            <ToggleButton value="revenue" aria-label="revenue">
              Receita
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={handleChartTypeChange}
            size="small"
            aria-label="chart type"
          >
            <ToggleButton value="pie" aria-label="pie chart">
              <PieChartIcon size={18} />
            </ToggleButton>
            <ToggleButton value="bar" aria-label="bar chart">
              <BarChartIcon size={18} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {startDate ? new Date(startDate).toLocaleDateString() : ""} -{" "}
        {endDate ? new Date(endDate).toLocaleDateString() : ""}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <Box sx={{ height: 350, width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === "pie" ? (
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey={viewMode === "quantity" ? "value" : "revenue"}
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              ) : (
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    tickFormatter={(value) =>
                      viewMode === "revenue" ? `R$ ${value}` : value
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey={viewMode === "quantity" ? "value" : "revenue"}
                    name={viewMode === "quantity" ? "Quantidade" : "Receita"}
                    fill={theme.palette.primary.main}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Resumo
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total de Categorias
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {data.length}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total de Produtos Vendidos
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {totalQuantity}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Receita Total
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                R$ {totalRevenue.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
